/**
 * Occupancy Grid Service
 *
 * Provides spatial awareness calculations for the kitchen floor plan.
 * Used for:
 * - Collision detection during drag/drop
 * - AI placement validation
 * - Work triangle calculations
 * - Kashrut zone separation
 */

import type {
  Point2D,
  Point3D,
  WallSegment,
  PlacedVolume,
  OccupancyCell,
  PlacementValidation,
  ProductResponse,
  RoomConfig,
} from "@byk/shared";
import { DEFAULT_OCCUPANCY_CELL } from "@byk/shared";

// =============================================================================
// Types
// =============================================================================

export type OccupancyGrid = Map<string, OccupancyCell>;

export interface OccupancyConfig {
  cellSize: number; // cm per cell (default 10)
  room: RoomConfig;
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Generate a unique key for a grid cell
 */
export function cellKey(x: number, y: number): string {
  return `${x},${y}`;
}

/**
 * Parse a cell key back to coordinates
 */
export function parseKey(key: string): Point2D {
  const [x, y] = key.split(",").map(Number);
  return { x, y };
}

/**
 * Snap a coordinate to the grid
 */
export function snapToCell(value: number, cellSize: number): number {
  return Math.floor(value / cellSize) * cellSize;
}

/**
 * Get all cell coordinates covered by a rectangular area
 */
export function getCellsInRect(
  x: number,
  y: number,
  width: number,
  depth: number,
  cellSize: number
): Point2D[] {
  const cells: Point2D[] = [];
  const startX = snapToCell(x, cellSize);
  const startY = snapToCell(y, cellSize);
  const endX = x + width;
  const endY = y + depth;

  for (let cx = startX; cx < endX; cx += cellSize) {
    for (let cy = startY; cy < endY; cy += cellSize) {
      cells.push({ x: cx, y: cy });
    }
  }

  return cells;
}

// =============================================================================
// Wall Occupancy
// =============================================================================

/**
 * Get all cells occupied by a wall segment.
 * Uses Bresenham-like algorithm for thick lines.
 */
export function getCellsCoveredByWall(
  wall: WallSegment,
  cellSize: number
): Point2D[] {
  const cells: Point2D[] = [];
  const thickness = wall.thickness || 15;

  // Calculate wall direction and length
  const dx = wall.end.x - wall.start.x;
  const dy = wall.end.y - wall.start.y;
  const length = Math.sqrt(dx * dx + dy * dy);

  if (length === 0) return cells;

  // Normalize direction
  const nx = dx / length;
  const ny = dy / length;

  // Perpendicular for thickness
  const px = -ny;
  const py = nx;

  // Walk along the wall, marking cells
  const steps = Math.ceil(length / cellSize);

  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const centerX = wall.start.x + dx * t;
    const centerY = wall.start.y + dy * t;

    // Mark cells across the thickness
    const thicknessSteps = Math.ceil(thickness / cellSize);
    for (let j = -thicknessSteps / 2; j <= thicknessSteps / 2; j++) {
      const x = snapToCell(centerX + px * j * cellSize, cellSize);
      const y = snapToCell(centerY + py * j * cellSize, cellSize);

      // Avoid duplicates
      if (!cells.some((c) => c.x === x && c.y === y)) {
        cells.push({ x, y });
      }
    }
  }

  return cells;
}

// =============================================================================
// Volume Occupancy
// =============================================================================

/**
 * Get all cells occupied by a placed volume, including front access zone.
 */
export function getCellsCoveredByVolume(
  volume: PlacedVolume,
  dimensions: { width: number; height: number; depth: number },
  cellSize: number,
  includeAccessZone = true
): { bodyCells: Point2D[]; accessCells: Point2D[] } {
  const { position, rotation = 0, semantics } = volume;

  // Apply rotation to dimensions (simplified: only 90-degree rotations)
  let effectiveWidth = dimensions.width;
  let effectiveDepth = dimensions.depth;

  if (rotation === 90 || rotation === 270) {
    effectiveWidth = dimensions.depth;
    effectiveDepth = dimensions.width;
  }

  // Get body cells
  const bodyCells = getCellsInRect(
    position.x,
    position.y,
    effectiveWidth,
    effectiveDepth,
    cellSize
  );

  // Get front access zone cells (if required)
  const accessCells: Point2D[] = [];

  if (includeAccessZone && semantics?.requiresFrontAccess) {
    const accessDepth = semantics.frontAccessDepth || 60;

    // Front access is in the +Y direction (relative to volume rotation)
    // For simplicity, assume front is always at the +Y side
    const accessY = position.y + effectiveDepth;

    const accessZoneCells = getCellsInRect(
      position.x,
      accessY,
      effectiveWidth,
      accessDepth,
      cellSize
    );

    accessCells.push(...accessZoneCells);
  }

  return { bodyCells, accessCells };
}

// =============================================================================
// Grid Building
// =============================================================================

export interface BuildGridParams {
  room: RoomConfig;
  walls: WallSegment[];
  volumes: PlacedVolume[];
  products: ProductResponse[];
  cellSize?: number;
}

/**
 * Build the complete occupancy grid from current floor plan state.
 * This is the main function called by the store.
 */
export function buildOccupancyGrid({
  room,
  walls,
  volumes,
  products,
  cellSize = 10,
}: BuildGridParams): OccupancyGrid {
  const grid: OccupancyGrid = new Map();

  // Helper to get or create cell
  const getCell = (x: number, y: number): OccupancyCell => {
    const key = cellKey(x, y);
    if (!grid.has(key)) {
      grid.set(key, { ...DEFAULT_OCCUPANCY_CELL });
    }
    return grid.get(key)!;
  };

  // 1. Mark walls
  for (const wall of walls) {
    const cells = getCellsCoveredByWall(wall, cellSize);
    for (const { x, y } of cells) {
      const cell = getCell(x, y);
      cell.occupied = true;
      cell.occupantId = wall.id;
      cell.occupantType = "wall";
      cell.isAccessiblePath = false;
      cell.distanceToWall = 0;
    }
  }

  // 2. Mark volumes and their access zones
  for (const volume of volumes) {
    const product = products.find((p) => p.id === volume.productId);
    if (!product || !product.dimensions) continue;

    const { bodyCells, accessCells } = getCellsCoveredByVolume(
      volume,
      {
        width: product.dimensions.width,
        height: product.dimensions.height,
        depth: product.dimensions.depth,
      },
      cellSize,
      true
    );

    // Mark body cells
    for (const { x, y } of bodyCells) {
      const cell = getCell(x, y);
      cell.occupied = true;
      cell.occupantId = volume.id;
      cell.occupantType = "volume";
      cell.isAccessiblePath = false;
    }

    // Mark access zone cells (can be walked through but blocks other objects)
    for (const { x, y } of accessCells) {
      const cell = getCell(x, y);
      // Only mark if not already occupied by something more solid
      if (!cell.occupied) {
        cell.occupied = true;
        cell.occupantId = volume.id;
        cell.occupantType = "access_zone";
        cell.accessZoneForId = volume.id;
        // Access zones are still accessible paths for walking
        cell.isAccessiblePath = true;
      }
    }
  }

  // 3. Calculate distance to walls for empty cells
  // (Simplified: just check if within room bounds)
  const roomCells = getCellsInRect(0, 0, room.width, room.depth, cellSize);
  for (const { x, y } of roomCells) {
    const cell = getCell(x, y);
    if (cell.distanceToWall === Infinity) {
      // Simple distance calculation (minimum distance to any edge)
      const distToLeft = x;
      const distToRight = room.width - x - cellSize;
      const distToTop = y;
      const distToBottom = room.depth - y - cellSize;
      cell.distanceToWall = Math.min(
        distToLeft,
        distToRight,
        distToTop,
        distToBottom
      );
    }
  }

  return grid;
}

// =============================================================================
// Placement Validation
// =============================================================================

/**
 * Check if a volume can be placed at the given position.
 */
export function validatePlacement(
  grid: OccupancyGrid,
  volumeId: string | null, // null for new placement preview
  position: Point3D,
  dimensions: { width: number; height: number; depth: number },
  rotation: number,
  semantics: PlacedVolume["semantics"],
  cellSize: number
): PlacementValidation {
  // Create a temporary volume for cell calculation
  const tempVolume: PlacedVolume = {
    id: volumeId || "temp",
    productId: "temp",
    position,
    rotation,
    zone: "prep",
    semantics,
  };

  const { bodyCells, accessCells } = getCellsCoveredByVolume(
    tempVolume,
    dimensions,
    cellSize,
    true
  );

  const conflictingCells: Array<{ x: number; y: number }> = [];
  const conflictingObjectIds = new Set<string>();

  // Check body cells for conflicts
  for (const { x, y } of bodyCells) {
    const key = cellKey(x, y);
    const cell = grid.get(key);

    if (cell?.occupied && cell.occupantId !== volumeId) {
      conflictingCells.push({ x, y });
      if (cell.occupantId) {
        conflictingObjectIds.add(cell.occupantId);
      }
    }
  }

  // Check access zone cells (only conflict with solid objects, not other access zones)
  for (const { x, y } of accessCells) {
    const key = cellKey(x, y);
    const cell = grid.get(key);

    if (
      cell?.occupied &&
      cell.occupantId !== volumeId &&
      cell.occupantType !== "access_zone"
    ) {
      conflictingCells.push({ x, y });
      if (cell.occupantId) {
        conflictingObjectIds.add(cell.occupantId);
      }
    }
  }

  if (conflictingCells.length > 0) {
    return {
      valid: false,
      reason: `Overlaps with ${conflictingObjectIds.size} object(s)`,
      conflictingCells,
      conflictingObjectIds: Array.from(conflictingObjectIds),
    };
  }

  return { valid: true };
}

// =============================================================================
// Query Functions
// =============================================================================

/**
 * Get a cell at the given coordinates.
 */
export function getCellAt(
  grid: OccupancyGrid,
  x: number,
  y: number,
  cellSize: number
): OccupancyCell {
  const snappedX = snapToCell(x, cellSize);
  const snappedY = snapToCell(y, cellSize);
  const key = cellKey(snappedX, snappedY);
  return grid.get(key) || { ...DEFAULT_OCCUPANCY_CELL };
}

/**
 * Get all available (unoccupied) cells in the room.
 */
export function getAvailableCells(
  grid: OccupancyGrid,
  room: RoomConfig,
  cellSize: number
): Point2D[] {
  const available: Point2D[] = [];

  for (let x = 0; x < room.width; x += cellSize) {
    for (let y = 0; y < room.depth; y += cellSize) {
      const key = cellKey(x, y);
      const cell = grid.get(key);
      if (!cell?.occupied) {
        available.push({ x, y });
      }
    }
  }

  return available;
}
